import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import PropertyCard from "@/components/listings/property-card";
import PropertyFiltersComponent, {
  PropertyFilters,
} from "@/components/property/property-filters";
import ListingsPagination from "@/components/listings/pagination";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { searchProperties } from "@/lib/property-service";
import { Property } from "@/types/types";

export default function PropertiesClient() {
  const [filters, setFilters] = useState<PropertyFilters>({
    search: "",
    types: [],
    minPrice: null,
    maxPrice: null,
    bedrooms: null,
    bathrooms: null,
    location: "",
    sortBy: "newest",
  });

  const [filteredListings, setFilteredListings] = useState<Property[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Get current listings for pagination
  const indexOfLastListing = currentPage * Number(itemsPerPage);
  const indexOfFirstListing = indexOfLastListing - Number(itemsPerPage);
  const currentListings = filteredListings.slice(
    indexOfFirstListing,
    indexOfLastListing
  );

  // Apply filters
  useEffect(() => {
    const applyFilters = async () => {
      setIsLoading(true);

      try {
        // Start with all properties
        const allPropertiesResult = await searchProperties({});

        let listings = allPropertiesResult.data;

        // Search filter
        if (filters.search) {
          const searchLower = filters.search.toLowerCase();
          listings = listings.filter(
            (p) =>
              p.title.toLowerCase().includes(searchLower) ||
              p.address.toLowerCase().includes(searchLower) ||
              p.description?.toLowerCase().includes(searchLower)
          );
        }

        // Property type filter
        if (filters.types.length > 0) {
          listings = listings.filter((p) => filters.types.includes(p.type));
        }

        // Price filters
        if (filters.minPrice !== null) {
          listings = listings.filter((p) => p.rent >= filters.minPrice!);
        }
        if (filters.maxPrice !== null) {
          listings = listings.filter((p) => p.rent <= filters.maxPrice!);
        }

        // Location filter
        if (filters.location) {
          listings = listings.filter((p) =>
            p.address.toLowerCase().includes(filters.location.toLowerCase())
          );
        }

        // Bedrooms filter
        if (filters.bedrooms !== null) {
          listings = listings.filter((p) => p.bedrooms >= filters.bedrooms!);
        }

        // Bathrooms filter
        if (filters.bathrooms !== null) {
          listings = listings.filter((p) => p.bathrooms >= filters.bathrooms!);
        }

        // Sort
        switch (filters.sortBy) {
          case "price_asc":
            listings.sort((a, b) => a.rent - b.rent);
            break;
          case "price_desc":
            listings.sort((a, b) => b.rent - a.rent);
            break;
          case "newest":
            listings.sort(
              (a, b) =>
                new Date(b.createdAt).getTime() -
                new Date(a.createdAt).getTime()
            );
            break;
          case "oldest":
            listings.sort(
              (a, b) =>
                new Date(a.createdAt).getTime() -
                new Date(b.createdAt).getTime()
            );
            break;
        }

        setFilteredListings(listings);
        setCurrentPage(1);
      } catch (error) {
        console.error("Error fetching properties:", error);
        setFilteredListings([]);
      } finally {
        setIsLoading(false);
      }
    };

    applyFilters();
  }, [filters]);

  // Handle pagination
  const paginate = (pageNumber: number): void => setCurrentPage(pageNumber);

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/">Home</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Properties</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex flex-col space-y-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold">Property Listings</h1>
          <p className="text-muted-foreground">
            {filteredListings.length} properties available
          </p>
        </div>

        <PropertyFiltersComponent
          filters={filters}
          onFiltersChange={setFilters}
        />
      </div>

      <div>
        {isLoading ? (
          <div className="flex justify-center items-center h-96">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredListings.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-96 text-center">
            <h3 className="text-xl font-semibold mb-2">No properties found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search filters to find more properties
            </p>
            <Button
              onClick={() =>
                setFilters({
                  search: "",
                  types: [],
                  minPrice: null,
                  maxPrice: null,
                  bedrooms: null,
                  bathrooms: null,
                  location: "",
                  sortBy: "newest",
                })
              }
              variant="outline"
            >
              Clear all filters
            </Button>
          </div>
        ) : (
          <div>
            <Tabs
              defaultValue="grid"
              value={view}
              onValueChange={setView}
              className="mb-6"
            >
              <TabsList className="grid w-[200px] grid-cols-2">
                <TabsTrigger value="grid">Grid</TabsTrigger>
                <TabsTrigger value="list">List</TabsTrigger>
              </TabsList>

              <TabsContent value="grid" className="mt-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {currentListings.map((listing) => (
                    <PropertyCard key={listing.id} listing={listing} />
                  ))}
                </div>
              </TabsContent>

              <TabsContent value="list" className="mt-6">
                <div className="space-y-4">
                  {currentListings.map((listing) => (
                    <PropertyCard
                      key={listing.id}
                      listing={listing}
                      view="list"
                    />
                  ))}
                </div>
              </TabsContent>
            </Tabs>

            <div className="mt-8">
              <ListingsPagination
                itemsPerPage={itemsPerPage}
                totalItems={filteredListings.length}
                paginate={paginate}
                currentPage={currentPage}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
